import { describeAddFailure } from '../add-trace.js';
import { traceCameraRemove, watchCameraRemoval } from '../remove-trace.js';
import { isTransient } from '@studio/models/device';
import { beginDeviceTeardown, clearDeviceDeleted } from './teardown.js';
import { refreshTrialCameraConfiguration } from '#~/api/billing/camera-access.js';
import type { Remove } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import type { Proxy } from '#~/agent/index.js';
import {
  beginSourceChange,
  broker,
  removeRetainedSource,
  waitForSourceRemoval
} from '#~/agent/broker.js';
import { depot } from '#~/depot/index.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';
// ENG-3716: a removed device must not keep a connect wait (or failure badge) alive.
import { clearVideoTrackWait } from '#~/livekit/track-wait.js';
import { tearDownPipelineIfNecessary } from '#~/mediadevices/pipeline.js';
import type { Device } from '@studio/models/device';

const removals = new WeakMap<Proxy, Map<Device['id'], Promise<void>>>();

/** F3: a retained/"Unavailable" row is in no live control, so locateProxy misses it. Find the owning
 *  agent from the depot room map instead. */
const retainedSourceAgent = (deviceId: Device['id']): Proxy['id'] | undefined => {
  for (const room of Object.values(depot.getState().rooms ?? {})) {
    const agentId = room?.devices?.[deviceId as never]?.agentId;
    if (agentId) return agentId;
  }
  return undefined;
};

/** Delete intent fences late restore work before requesting authority removal. */
export const remove: Remove = async (
  deviceId,
  token,
  _force = false,
  nonOptimistic = false,
  replaceDriven = false
) => {
  const startedAt = Date.now();
  traceCameraRemove('command.start', { deviceId, force: _force, nonOptimistic, replaceDriven });
  const proxy = locateProxy(deviceId);
  if (!proxy) {
    traceCameraRemove('command.no-proxy', { deviceId });
    // F3: the device is not in any live control. If it is a retained row the depot still knows its
    // agent — remove it from storage only (no live mutate) rather than resolving as a silent no-op.
    const agentId = retainedSourceAgent(deviceId);
    const retainedProxy = agentId ? broker.acquire(agentId) : undefined;
    if (retainedProxy) {
      await removeRetainedSource(retainedProxy, deviceId, token);
      // Promote a retained camera only after storage accepts the saved configuration.
      await refreshTrialCameraConfiguration(retainedProxy.roomId).catch((error) => {
        logger.warning('Camera policy refresh failed after removal', error);
      });
      // The storage-only path acknowledges persistence without a live mutation.
      traceCameraRemove('save.complete', { deviceId, agentId: retainedProxy.id });
      return;
    }
    logger.warning(`Device ${deviceId} proxy not found.`);
    return;
  }
  const pending = removals.get(proxy) ?? new Map<Device['id'], Promise<void>>();
  removals.set(proxy, pending);
  const existing = pending.get(deviceId);
  if (existing) return existing;
  const operation = (async () => {
    /*
     * ENG-4477: tombstone the device and register a pending-teardown flight
     * on its agent BEFORE the removal mutation goes out. Settings flows that
     * are still loading for this device (calibration auto-start, image/
     * control settings mutations) consult the tombstone and bail instead of
     * firing orphaned mutations that race state sync; the retry path
     * (re-discovery) awaits the flight instead of racing the torn-down
     * device. See teardown.ts for the full mechanism.
     */
    watchCameraRemoval(deviceId, proxy.id);
    traceCameraRemove('command.located', {
      deviceId,
      agentId: proxy.id,
      sessionId: proxy.getSessionId?.()
    });
    const settleTeardown = beginDeviceTeardown(deviceId, proxy.id);
    const change = beginSourceChange(proxy, deviceId, true);
    let success = false;
    let storageAccepted = false;
    try {
      const commit = async (): Promise<void> => {
        if (!change.current()) throw new DOMException('Source owner retired', 'AbortError');
        traceCameraRemove('mutation.start', {
          deviceId,
          agentId: proxy.id,
          optimistic: isTransient(deviceId) && !nonOptimistic
        });
        await proxy.mutate(
          {},
          {
            control: {
              device: { [deviceId]: { urx: '' } },
              studio: { device: { [deviceId]: {} } }
            }
          },
          // Keep the authoritative row visible when a persistent-source delete is rejected.
          isTransient(deviceId) && !nonOptimistic
        );
        if (!change.current()) throw new DOMException('Source owner retired', 'AbortError');
        traceCameraRemove('mutation.resolved', {
          deviceId,
          agentId: proxy.id,
          controlPresent: !!proxy.state.control?.device?.[deviceId]
        });
        await waitForSourceRemoval(proxy, deviceId);
        if (!change.current()) throw new DOMException('Source owner retired', 'AbortError');
      };
      if (isTransient(deviceId)) await commit();
      else
        await proxy.save(token, true, {
          deviceId,
          current: change.current,
          commit: async () => {
            storageAccepted = true;
            // Proxy.save invokes this only after storage acknowledges the pruned snapshot.
            // HIL teardown observes persistence independently of live authority confirmation.
            traceCameraRemove('save.complete', { deviceId, agentId: proxy.id });
            await commit();
          }
        });
      if (!change.current()) throw new DOMException('Source owner retired', 'AbortError');
      success = true;
      // Refresh saved camera order without making a completed removal fail on an uncertain read.
      await refreshTrialCameraConfiguration(proxy.roomId).catch((error) => {
        logger.warning('Camera policy refresh failed after removal', error);
      });
      await tearDownPipelineIfNecessary(deviceId);
    } catch (cause) {
      traceCameraRemove('command.failed', {
        deviceId,
        elapsedMs: Date.now() - startedAt,
        failureCodes: describeAddFailure(cause).codes.join(',')
      });
      const message = success
        ? `Device removed, but local cleanup failed: ${deviceId}`
        : storageAccepted
          ? `Unable to remove device: ${deviceId}. Saved configuration was updated, but live removal was not confirmed. Retry removal.`
          : `Unable to remove device: ${deviceId}`;
      throw new StudioError(message, { cause });
    } finally {
      // Per rule: settle-every-terminal-branch (ai.knowledge/global) — a failed delete releases its
      // intent, and the ENG-4477 teardown flight settles so discovery waiters are never stranded.
      change.finish(success);
      settleTeardown();
      traceCameraRemove('command.settled', {
        deviceId,
        agentId: proxy.id,
        elapsedMs: Date.now() - startedAt
      });
      // F5 (settle-every-terminal-branch): a failed non-optimistic delete leaves the device live and
      // visible, so its tombstone must be cleared — otherwise every later settings/image mutation on
      // that still-present camera is silently dropped. Keep the tombstone only when the source is gone.
      if (!success && proxy.state.control?.device?.[deviceId]) clearDeviceDeleted(deviceId);
      // ENG-3716: a removed source must not keep a connect wait (or failure badge) alive. A failed
      // persistent delete is non-optimistic and leaves the source live, so its wait is kept.
      if (success || !proxy.state.control?.device?.[deviceId]) clearVideoTrackWait(deviceId);
    }
  })();
  pending.set(deviceId, operation);
  try {
    await operation;
  } finally {
    if (pending.get(deviceId) === operation) pending.delete(deviceId);
  }
};
